import { type ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, DataTable, EmptyState, Skeleton, type DataTableColumn, useToast } from '@aps/ui';
import {
  CreateStaffSchema,
  ValidationDetailSchema,
  type RoleResponse,
  type StaffListItem,
} from '@aps/contracts';
import { ApiError, api } from '../lib/api.js';
import { messages, type Messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { QueueError } from './WorkQueueScreen.js';

const emptyForm = { staffLogin: '', firstName: '', password: '', roleId: '', shopId: '' };
type StaffField = keyof typeof emptyForm;
type FieldErrors = Partial<Record<StaffField, string>>;

function fieldFromPath(path: readonly (string | number)[]): StaffField | null {
  switch (path[0]) {
    case 'staffLogin':
    case 'firstName':
    case 'password':
    case 'shopId':
      return path[0];
    case 'roleIds':
      return 'roleId';
    default:
      return null;
  }
}

function fieldMessage(field: StaffField, t: Messages): string {
  switch (field) {
    case 'staffLogin':
      return t.usersLoginInvalid;
    case 'firstName':
      return t.usersNameInvalid;
    case 'password':
      return t.usersPasswordInvalid;
    case 'roleId':
      return t.usersRoleRequired;
    case 'shopId':
      return t.usersShopRequired;
  }
}

function toFieldErrors(
  issues: readonly { path: readonly (string | number)[] }[],
  t: Messages,
): FieldErrors {
  const result: FieldErrors = {};
  for (const issue of issues) {
    const field = fieldFromPath(issue.path);
    if (field !== null) result[field] = fieldMessage(field, t);
  }
  return result;
}

/**
 * Xodimlar va rollar.
 *
 * Xodim O'ZI RO'YXATDAN O'TMAYDI — uni Owner yaratadi
 * (`docs/06-OCHIQ-SAVOLLAR.md`, 10-savol). Shuning uchun bu ekranda
 * "ro'yxatdan o'tish" emas, YARATISH bor.
 *
 * Rol berish va olib qo'yish auditga tushadi: "bu odamga Owner
 * rolini kim berdi?" savoliga javob shu yerdan topiladi.
 */
export function UsersScreen(): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Boshqariladigan holat — loyihaning boshqa formalari ham shunday
  // (`ProductFormScreen`).
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // Jadvalda SHOP rol tanlangan qator: rol do'kon tanlangandan KEYIN beriladi.
  const [pendingShopRole, setPendingShopRole] = useState<{
    userId: string;
    roleId: string;
  } | null>(null);

  const setField = (field: StaffField, value: string): void => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
    setError(null);
  };

  const staff = useQuery({
    queryKey: ['staff', search],
    queryFn: () => api.staff(search),
  });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.roles() });
  // Faqat FAOL do'konlar — server nofaolga biriktirishni baribir rad etadi.
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => api.shops() });

  /*
   * TASK-007-B (D05): do'kon faqat SHOP rol uchun so'raladi. Tasniflanmagan
   * (`scope = null`) rol ko'rinadi, lekin tanlanmaydi — server uni rad etadi.
   */
  const scopeOf = (roleId: string): RoleResponse['scope'] | undefined =>
    (roles.data ?? []).find((role) => role.id === roleId)?.scope;
  const roleOption = (role: RoleResponse): ReactNode => (
    <option key={role.id} value={role.id} disabled={role.scope === null}>
      {role.scope === null ? `${role.nameUz} (${t.usersRoleUnclassified})` : role.nameUz}
    </option>
  );
  const shopOptions = (shops.data ?? []).map((shop) => (
    <option key={shop.id} value={shop.id}>
      {shop.nameUz}
    </option>
  ));

  const invalidate = async (): Promise<void> => {
    await client.invalidateQueries({ queryKey: ['staff'] });
  };

  const createStaff = useMutation({
    mutationFn: (dto: Record<string, unknown>) => api.createStaff(dto),
    onSuccess: () => {
      setCreating(false);
      setForm(emptyForm);
      setFieldErrors({});
      setError(null);
      toast.show(t.toastAdded, 'success');
      void invalidate();
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiError && cause.payload.code === 'VALIDATION_FAILED') {
        const uniqueDetail = cause.payload.details;
        if (
          typeof uniqueDetail === 'object' &&
          uniqueDetail !== null &&
          'field' in uniqueDetail &&
          uniqueDetail.field === 'staffLogin'
        ) {
          setFieldErrors({ staffLogin: t.usersLoginTaken });
          setError(null);
          return;
        }
        const details = ValidationDetailSchema.array().safeParse(cause.payload.details);
        if (details.success) {
          const errors = toFieldErrors(details.data, t);
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setError(null);
            return;
          }
        }
      }
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const assignRole = useMutation({
    mutationFn: (input: { userId: string; roleId: string; shopId?: string }) =>
      api.assignRole(input.userId, {
        roleId: input.roleId,
        ...(input.shopId === undefined ? {} : { shopId: input.shopId }),
      }),
    onSuccess: () => {
      // Rol berish/olib qo'yish AUDITGA yoziladi — "bu odamga Owner
      // rolini kim berdi?" savoliga javob shundan topiladi. Operator
      // buni bilib turgani ma'qul.
      toast.show(t.toastRoleChanged, 'success');
      return invalidate();
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const removeRole = useMutation({
    mutationFn: (input: { userId: string; roleId: string }) =>
      api.removeRole(input.userId, input.roleId),
    onSuccess: () => {
      // Rol berish/olib qo'yish AUDITGA yoziladi — "bu odamga Owner
      // rolini kim berdi?" savoliga javob shundan topiladi. Operator
      // buni bilib turgani ma'qul.
      toast.show(t.toastRoleChanged, 'success');
      return invalidate();
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const columns: DataTableColumn<StaffListItem>[] = [
    {
      key: 'login',
      header: t.usersLogin,
      render: (row) => row.staffLogin ?? '—',
    },
    {
      key: 'name',
      header: t.usersName,
      render: (row) => [row.firstName, row.lastName].filter((part) => part !== null).join(' '),
    },
    {
      key: 'state',
      header: t.usersState,
      render: (row) =>
        row.isActive ? (
          <span className="aps-muted">{t.usersActive}</span>
        ) : (
          // Ishdan ketgan xodim ro'yxatdan YO'QOLMAYDI: uning roli
          // qolib ketganini kimdir ko'rishi kerak.
          <span className="adm-badge adm-badge--pending">{t.usersInactive}</span>
        ),
    },
    {
      key: 'roles',
      header: t.usersRoles,
      render: (row) => (
        <div className="adm-filters__chips">
          {row.roles.length === 0 && <span className="aps-muted">{t.usersNoRole}</span>}
          {row.roles.map((role) => (
            <Button
              key={role.roleId}
              variant="ghost"
              size="sm"
              disabled={removeRole.isPending}
              onClick={() => {
                removeRole.mutate({ userId: row.id, roleId: role.roleId });
              }}
            >
              {role.nameUz}
              {role.shopNameUz === null ? '' : ` · ${role.shopNameUz}`} ✕
            </Button>
          ))}
        </div>
      ),
    },
    {
      key: 'add',
      header: t.usersAddRole,
      render: (row) => {
        const pending = pendingShopRole?.userId === row.id ? pendingShopRole : null;
        return (
          <div className="adm-filters__chips">
            <select
              className="aps-field__input"
              aria-label={t.usersAddRole}
              value={pending?.roleId ?? ''}
              disabled={assignRole.isPending}
              onChange={(event) => {
                const roleId = event.target.value;
                if (roleId === '') {
                  setPendingShopRole(null);
                } else if (scopeOf(roleId) === 'SHOP') {
                  setPendingShopRole({ userId: row.id, roleId });
                } else {
                  setPendingShopRole(null);
                  assignRole.mutate({ userId: row.id, roleId });
                }
              }}
            >
              <option value="">{t.productPick}</option>
              {(roles.data ?? [])
                .filter((role) => !row.roles.some((held) => held.roleId === role.id))
                .map(roleOption)}
            </select>
            {pending !== null && (
              <select
                className="aps-field__input"
                aria-label={t.usersShop}
                value=""
                disabled={assignRole.isPending}
                onChange={(event) => {
                  if (event.target.value === '') return;
                  setPendingShopRole(null);
                  assignRole.mutate({ ...pending, shopId: event.target.value });
                }}
              >
                <option value="">{t.productPick}</option>
                {shopOptions}
              </select>
            )}
          </div>
        );
      },
    },
  ];

  const needsShop = scopeOf(form.roleId) === 'SHOP';

  const submit = (): void => {
    const result = CreateStaffSchema.safeParse({
      staffLogin: form.staffLogin,
      password: form.password,
      firstName: form.firstName,
      roleIds: form.roleId === '' ? [] : [form.roleId],
      ...(needsShop && form.shopId !== '' ? { shopId: form.shopId } : {}),
    });
    const errors = result.success ? {} : toFieldErrors(result.error.issues, t);
    if (needsShop && form.shopId === '') errors.shopId = t.usersShopRequired;
    setFieldErrors(errors);
    if (!result.success || Object.keys(errors).length > 0) {
      setError(null);
      return;
    }
    createStaff.mutate(result.data);
  };

  return (
    <section className="adm-page">
      <header className="adm-page__header">
        <h1 className="adm-page__title">{t.navUsers}</h1>
        <Button
          size="sm"
          disabled={createStaff.isPending}
          onClick={() => {
            setCreating((value) => !value);
            setForm(emptyForm);
            setFieldErrors({});
            setError(null);
          }}
        >
          {creating ? t.cancel : t.usersCreate}
        </Button>
      </header>

      {creating && (
        <div className="adm-panel">
          <h2 className="adm-panel__title">{t.usersCreate}</h2>
          <div className="adm-panel__body">
            <p className="aps-field__hint">{t.usersCreateHint}</p>
            {/*
              Yorliq `htmlFor` bilan bog'lanadi, izoh esa
              `aria-describedby` bilan: ikkalasi `<label>` ICHIDA
              bo'lsa, ekran o'quvchi maydon NOMI sifatida butun izohni
              ham o'qib berardi.
            */}
            <div className="aps-field">
              <label className="aps-field__label" htmlFor="staffLogin">
                {t.loginField}
              </label>
              <input
                id="staffLogin"
                className="aps-field__input"
                aria-invalid={fieldErrors.staffLogin !== undefined}
                aria-describedby={
                  fieldErrors.staffLogin === undefined ? undefined : 'staffLoginError'
                }
                value={form.staffLogin}
                onChange={(event) => {
                  setField('staffLogin', event.target.value);
                }}
              />
              {fieldErrors.staffLogin && (
                <p className="aps-error" id="staffLoginError">
                  {fieldErrors.staffLogin}
                </p>
              )}
            </div>
            <div className="aps-field">
              <label className="aps-field__label" htmlFor="firstName">
                {t.usersName}
              </label>
              <input
                id="firstName"
                className="aps-field__input"
                aria-invalid={fieldErrors.firstName !== undefined}
                aria-describedby={
                  fieldErrors.firstName === undefined ? undefined : 'firstNameError'
                }
                value={form.firstName}
                onChange={(event) => {
                  setField('firstName', event.target.value);
                }}
              />
              {fieldErrors.firstName && (
                <p className="aps-error" id="firstNameError">
                  {fieldErrors.firstName}
                </p>
              )}
            </div>
            <div className="aps-field">
              <label className="aps-field__label" htmlFor="password">
                {t.passwordField}
              </label>
              <input
                id="password"
                type="password"
                className="aps-field__input"
                aria-invalid={fieldErrors.password !== undefined}
                aria-describedby={
                  fieldErrors.password === undefined ? 'passwordHint' : 'passwordHint passwordError'
                }
                value={form.password}
                onChange={(event) => {
                  setField('password', event.target.value);
                }}
              />
              <p className="aps-field__hint" id="passwordHint">
                {t.usersPasswordHint}
              </p>
              {fieldErrors.password && (
                <p className="aps-error" id="passwordError">
                  {fieldErrors.password}
                </p>
              )}
            </div>
            <div className="aps-field">
              <label className="aps-field__label" htmlFor="roleId">
                {t.usersRoles}
              </label>
              <select
                id="roleId"
                className="aps-field__input"
                aria-invalid={fieldErrors.roleId !== undefined}
                aria-describedby={fieldErrors.roleId === undefined ? undefined : 'roleIdError'}
                value={form.roleId}
                onChange={(event) => {
                  setField('roleId', event.target.value);
                  setField('shopId', '');
                }}
              >
                <option value="">{t.productPick}</option>
                {(roles.data ?? []).map(roleOption)}
              </select>
              {fieldErrors.roleId && (
                <p className="aps-error" id="roleIdError">
                  {fieldErrors.roleId}
                </p>
              )}
            </div>
            {needsShop && (
              <div className="aps-field">
                <label className="aps-field__label" htmlFor="shopId">
                  {t.usersShop}
                </label>
                <select
                  id="shopId"
                  className="aps-field__input"
                  aria-describedby={
                    fieldErrors.shopId === undefined ? 'shopIdHint' : 'shopIdHint shopIdError'
                  }
                  aria-invalid={fieldErrors.shopId !== undefined}
                  value={form.shopId}
                  onChange={(event) => {
                    setField('shopId', event.target.value);
                  }}
                >
                  <option value="">{t.productPick}</option>
                  {shopOptions}
                </select>
                <p className="aps-field__hint" id="shopIdHint">
                  {t.usersShopHint}
                </p>
                {fieldErrors.shopId && (
                  <p className="aps-error" id="shopIdError">
                    {fieldErrors.shopId}
                  </p>
                )}
              </div>
            )}
            <Button disabled={createStaff.isPending} onClick={submit}>
              {createStaff.isPending ? t.signingIn : t.productSave}
            </Button>
          </div>
        </div>
      )}

      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}

      <div className="adm-filters">
        <input
          className="aps-field__input"
          aria-label={t.usersSearch}
          placeholder={t.usersSearch}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </div>

      {staff.isPending && <Skeleton height="220px" label={t.loading} />}
      {staff.isError && (
        <QueueError
          message={staff.error instanceof ApiError ? staff.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void staff.refetch()}
        />
      )}
      {staff.isSuccess && (
        <DataTable
          caption={t.navUsers}
          columns={columns}
          rows={staff.data}
          rowKey={(row) => row.id}
          empty={<EmptyState title={t.usersEmpty} />}
        />
      )}
    </section>
  );
}
